const LIGHT_FOREGROUND = '#ffffff';
const DARK_FOREGROUND = '#0f172a';

/** Minimum WCAG AA contrast ratio for normal-sized text. */
const MIN_CONTRAST_RATIO = 4.5;

interface Rgb {
	r: number;
	g: number;
	b: number;
}

function channelToLinear(channel: number): number {
	const normalized = channel / 255;
	return normalized <= 0.03928
		? normalized / 12.92
		: Math.pow((normalized + 0.055) / 1.055, 2.4);
}

function relativeLuminance({ r, g, b }: Rgb): number {
	return 0.2126 * channelToLinear(r)
		+ 0.7152 * channelToLinear(g)
		+ 0.0722 * channelToLinear(b);
}

function contrastRatio(luminanceA: number, luminanceB: number): number {
	const lighter = Math.max(luminanceA, luminanceB);
	const darker = Math.min(luminanceA, luminanceB);
	return (lighter + 0.05) / (darker + 0.05);
}

function parseHexColor(value: string): Rgb | null {
	const hex = value.trim().replace(/^#/, '');
	if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(hex)) {
		return null;
	}

	const normalized = hex.length === 3
		? hex.split('').map((char) => char + char).join('')
		: hex;

	return {
		r: Number.parseInt(normalized.slice(0, 2), 16),
		g: Number.parseInt(normalized.slice(2, 4), 16),
		b: Number.parseInt(normalized.slice(4, 6), 16),
	};
}

function parseRgbColor(value: string): Rgb | null {
	const match = value.trim().match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
	if (!match) {
		return null;
	}

	return {
		r: Number.parseFloat(match[1]),
		g: Number.parseFloat(match[2]),
		b: Number.parseFloat(match[3]),
	};
}

export function parseCssColor(value: string): Rgb | null {
	const trimmed = value.trim();
	if (!trimmed) {
		return null;
	}

	if (trimmed.startsWith('#')) {
		return parseHexColor(trimmed);
	}

	if (trimmed.startsWith('rgb')) {
		return parseRgbColor(trimmed);
	}

	return null;
}

/** Pick a light or dark foreground that meets WCAG AA contrast on the given background. */
export function pickContrastingForeground(backgroundColor: string): string {
	const background = parseCssColor(backgroundColor);
	if (!background) {
		return LIGHT_FOREGROUND;
	}

	const backgroundLuminance = relativeLuminance(background);
	const lightContrast = contrastRatio(backgroundLuminance, relativeLuminance({ r: 255, g: 255, b: 255 }));
	const darkContrast = contrastRatio(backgroundLuminance, relativeLuminance({ r: 15, g: 23, b: 42 }));

	const lightMeetsTarget = lightContrast >= MIN_CONTRAST_RATIO;
	const darkMeetsTarget = darkContrast >= MIN_CONTRAST_RATIO;

	if (lightMeetsTarget && !darkMeetsTarget) {
		return LIGHT_FOREGROUND;
	}

	if (darkMeetsTarget && !lightMeetsTarget) {
		return DARK_FOREGROUND;
	}

	return lightContrast >= darkContrast ? LIGHT_FOREGROUND : DARK_FOREGROUND;
}
